import { useContext, useEffect, useRef, useCallback } from "react";
import {
  ActionDependant,
  GlobalDataContext,
  useStore,
  TemplateContext,
  dataLocationToPath,
} from "@ea-lab/reactive-json";
import axios from "axios";
import { isEqual } from "lodash";
import { joinSyncGroup, broadcastToGroup, getOwner } from "@ea-lab/reactive-json";
import { BuilderContext } from "../BuilderContextInstance";

/**
 * PROJECT OVERRIDE of reactive-json's core `DataSync`.
 * Registered as `element.DataSync` in builderPlugins (overrides the core one).
 *
 * ─ Why this still exists after the reactive-json 2.6.0 bump ─────────────────
 * This override is now `core DataSync (2.6.0)` + ONE builder-only layer. Two of
 * its three original concerns shipped upstream in 2.6.0 and are no longer a
 * fork — the code below merely MIRRORS them (kept in sync line-for-line):
 *
 *   1. `item_url` baseline (was the original reason for the override): a
 *      syncable fetched into the store after mount (e.g. a `builder_fragment`
 *      loaded via `additionalDataSource` into a `ReactiveJsonSubroot`) would go
 *      empty → loaded and POST itself back on page load — before CSRF was ready
 *      → 403. Fix: tell server-origin (re)loads from user edits by the
 *      syncable's identity (`item_url`); an appearing/changing `item_url` is a
 *      server load → baseline without syncing, only a STABLE `item_url` + a
 *      `data` change is a real edit. → shipped in core 2.6.0.
 *   2. `mergeKey` shared-syncable groups (live-mirror several mounts of the same
 *      resource, single owner-writer, no duplicate/stale POST). → shipped in
 *      core 2.6.0; exported directly from `@ea-lab/reactive-json` since 2.7.0.
 *
 * The ONLY reason the override is NOT yet deletable is the builder manual-save
 * cadence (Lot S), which is NOT in the lib:
 *
 *   3. Manual vs autosave via `BuilderContext` (`autoSave` / `flushVersion` /
 *      `markContentDirty`). In manual mode (builder default) a genuine edit is
 *      held — flagged dirty for the Save button + unload guard — and persisted
 *      only when the global Save bumps `flushVersion` (owner-only POST for a
 *      shared group → one coalesced write). Outside the builder there is no
 *      BuilderContext provider → `autoSave` defaults true → behaves EXACTLY
 *      like core 2.6.0 (autosave on idle).
 *
 * Not upstreamed because it is half app-concern (dirty tracking + beforeunload
 * UX live in BuilderContext, not in a sync component) and half a non-trivial lib
 * change: the live broadcast fires on every store change, which in the builder
 * means on blur/commit (free) but in a generic `onChange` RJ form would mean
 * per-keystroke — so a generic `mode: 'manual'` would have to cadence the
 * broadcast by mode. Until that lands upstream, this layer stays here.
 *
 * Maintenance: when editing concerns 1–2, keep them identical to
 * lib/component/element/special/DataSync.jsx (2.6.0); concern 3 is ours.
 */
const toStorePath = (rawPath) => {
  if (rawPath === "data") return "";
  if (rawPath && rawPath.startsWith("data.")) return rawPath.substring(5);
  return rawPath;
};

export const DataSync = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const store = useStore();

  // Builder content-save cadence (Lot S). The context crosses the subroot's
  // ReactiveJsonRoot boundary, so a DataSync mounted inside any unit reads it.
  // Outside the builder (lib tests, other apps) there is no provider → behave
  // exactly like the upstream component (autosave on idle).
  const builder = useContext(BuilderContext);
  const autoSave = builder ? builder.autoSave : true;
  const flushVersion = builder ? builder.flushVersion : 0;
  const markContentDirty = builder?.markContentDirty;

  const lastAttemptedDataRef = useRef(undefined);
  const lastServerResponseRef = useRef(null);
  const lastItemUrlRef = useRef(undefined);
  const timeoutRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const isSyncingRef = useRef(false);
  const retryCountRef = useRef(0);
  const eventTargetRef = useRef(null);
  const performSyncRef = useRef(null);

  const mode = props.mode || 'onIdle';
  const idleDelay = props.idleDelay || 1000;
  const maxRetries = props.maxRetries ?? 0;
  const retryDelay = props.retryDelay ?? 5000;

  let resolvedPath = null;
  try {
    resolvedPath = toStorePath(dataLocationToPath({
      dataLocation: props.path,
      currentPath: templateContext.templatePath,
      globalDataContext,
      templateContext,
    }));
  } catch (e) {
    console.error("DataSync: Invalid path", props.path, e);
  }

  let triggerPath = null;
  if (props.trigger) {
    try {
      triggerPath = toStorePath(dataLocationToPath({
        dataLocation: props.trigger,
        currentPath: templateContext.templatePath,
        globalDataContext,
        templateContext,
      }));
    } catch (e) {
      console.error("DataSync: Invalid trigger path", props.trigger, e);
    }
  }

  // Shared-syncable ("merge") grouping. When `mergeKey` resolves to a stable
  // identity (e.g. the submission_url), every DataSync yielding the same value
  // forms a group: a real edit on one is broadcast to the others (mirrored into
  // their store, echo-suppressed), so they update live and never re-POST — the
  // editing instance is the only one that persists.
  let mergeKeyValue = null;
  if (props.mergeKey && store) {
    try {
      mergeKeyValue = store.get(toStorePath(dataLocationToPath({
        dataLocation: props.mergeKey,
        currentPath: templateContext.templatePath,
        globalDataContext,
        templateContext,
      })));
    } catch (e) { /* unresolved yet — joins once available */ }
  }
  else if (store && resolvedPath !== null) {
    // Défaut : l'identité du syncable lui-même. Deux montages de la MÊME entité
    // (un bloc partagé posé deux fois, un fragment embarqué plusieurs fois)
    // forment donc un groupe sans que le YAML ait à le déclarer — ce qui est
    // exactement la sémantique voulue, et évite d'ajouter `mergeKey:` aux ~100
    // définitions de blocs. Un `mergeKey` explicite reste prioritaire.
    try {
      mergeKeyValue = store.get(resolvedPath)?.item_url ?? null;
    } catch { /* pas encore chargé — rejoint le groupe dès que dispo */ }
  }
  const mergeKeyRef = useRef(null);
  mergeKeyRef.current = mergeKeyValue;

  // Stable member handle for this instance: apply remote data into our store as
  // a baseline so our own change-watcher ignores it (no re-broadcast, no POST).
  const memberRef = useRef(null);
  if (memberRef.current === null) {
    memberRef.current = {
      // Manual mode: set on a genuine edit, cleared once flushed. For a shared
      // group it lives on the OWNER (the single writer), so one flush → one POST.
      pending: false,
      // Apply remote data into our store, suppressing our own change-watcher
      // (no re-broadcast, no POST) — a live mirror of a sibling's edit.
      applyRemote: (data) => {
        if (resolvedPath === null) return;
        lastAttemptedDataRef.current = data;
        const cur = store.get(resolvedPath) || {};
        store.set(resolvedPath, { ...cur, data });
      },
      // Owner-only: (re)schedule the single debounced POST, reading the LATEST
      // shared data at fire time (never a stale captured object).
      requestSync: () => {
        if (resolvedPath === null) return;
        if (mode === 'immediate') {
          performSyncRef.current?.(store.get(resolvedPath));
          return;
        }
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          performSyncRef.current?.(store.get(resolvedPath));
        }, idleDelay);
      },
    };
  }

  // Join/leave the group when the resolved key appears or changes.
  useEffect(() => {
    if (!mergeKeyValue || resolvedPath === null) return;
    return joinSyncGroup(mergeKeyValue, memberRef.current);
  }, [mergeKeyValue, resolvedPath]);

  const performSync = useCallback(async (currentObject) => {
    if (!currentObject || !currentObject.submission_url) {
      if (currentObject) {
        console.warn("DataSync: Missing submission_url in data object", currentObject);
      }
      return;
    }

    if (isSyncingRef.current) return;
    if (retryCountRef.current > maxRetries) return;

    isSyncingRef.current = true;
    retryCountRef.current++;
    lastAttemptedDataRef.current = currentObject.data;

    store.set(`${resolvedPath}.status`, { type: "info", message: "Synchronisation en cours..." });

    try {
      const { submission_url } = currentObject;
      const response = await axios.post(submission_url, currentObject);
      const responseData = response.data;

      lastServerResponseRef.current = responseData;
      retryCountRef.current = 0;
      store.set(resolvedPath, responseData);

      // Mirror the authoritative server data (enriched) to sibling syncables.
      if (mergeKeyRef.current) {
        broadcastToGroup(mergeKeyRef.current, memberRef.current, responseData?.data);
      }

      eventTargetRef.current?.dispatchEvent(new CustomEvent("syncSuccess", {
        bubbles: true,
        detail: { value: responseData, responseContext: { status: response.status } },
      }));
    } catch (error) {
      console.error("DataSync error:", error);
      const serverBody = error.response?.data;

      if (serverBody && typeof serverBody === "object" && serverBody.status) {
        lastServerResponseRef.current = serverBody;
        retryCountRef.current = 0;
        store.set(resolvedPath, serverBody);
        eventTargetRef.current?.dispatchEvent(new CustomEvent("syncError", {
          bubbles: true,
          detail: { value: serverBody, responseContext: { status: error.response?.status } },
        }));
        isSyncingRef.current = false;
        return;
      }

      store.set(`${resolvedPath}.status`, { type: "error", message: error.message || "Unknown error" });
      eventTargetRef.current?.dispatchEvent(new CustomEvent("syncError", {
        bubbles: true,
        detail: { value: serverBody, responseContext: { status: error.response?.status } },
      }));

      const isServerError = error.response && error.response.status >= 500;
      const isNetworkOutage = !error.response && typeof navigator !== "undefined" && !navigator.onLine;
      if ((isServerError || isNetworkOutage) && retryCountRef.current <= maxRetries) {
        retryTimeoutRef.current = setTimeout(() => {
          performSync(store.get(resolvedPath));
        }, retryDelay);
      }
    } finally {
      isSyncingRef.current = false;
    }
  }, [store, resolvedPath, maxRetries, retryDelay]);

  // Keep the member's requestSync() pointed at the latest performSync.
  performSyncRef.current = performSync;

  // Watch for data changes
  useEffect(() => {
    if (!store || resolvedPath === null) return;

    // Baseline identity + data present at subscribe time (synchronous mounts).
    const initialObject = store.get(resolvedPath);
    if (initialObject) {
      lastItemUrlRef.current = initialObject.item_url ?? initialObject.submission_url;
      lastAttemptedDataRef.current = initialObject.data;
    }

    const handleDataChange = () => {
      const currentObject = store.get(resolvedPath);
      if (!currentObject) return;
      if (currentObject === lastServerResponseRef.current) return;

      const currentData = currentObject.data;

      // Server-origin (re)load → baseline, don't sync.
      const currentItemUrl = currentObject.item_url ?? currentObject.submission_url;
      if (currentItemUrl !== lastItemUrlRef.current) {
        lastItemUrlRef.current = currentItemUrl;
        lastAttemptedDataRef.current = currentData;
        return;
      }

      if (lastAttemptedDataRef.current !== undefined && isEqual(currentData, lastAttemptedDataRef.current)) return;
      if (lastServerResponseRef.current && isEqual(currentData, lastServerResponseRef.current.data)) return;

      retryCountRef.current = 0;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      // A genuine content edit. In manual mode (builder default) it must NOT
      // persist now: flag the content dirty (drives the Save button + unload
      // guard) and wait for the global flush. The live mirror to siblings still
      // happens so all mounts stay visually in sync regardless of cadence.
      const manual = !autoSave;

      // Shared-syncable group: mirror the edit live to siblings (echo-
      // suppressed). In auto mode, delegate persistence to the single OWNER,
      // which (re)schedules its one debounced POST reading the LATEST data —
      // rapid alternating edits coalesce into one correct write. In manual mode,
      // mark the OWNER pending so the flush produces that single coalesced POST.
      if (mergeKeyRef.current) {
        broadcastToGroup(mergeKeyRef.current, memberRef.current, currentData);
        const owner = getOwner(mergeKeyRef.current) || memberRef.current;
        if (manual) {
          owner.pending = true;
          markContentDirty?.();
        } else {
          owner.requestSync();
        }
        return;
      }

      if (manual) {
        memberRef.current.pending = true;
        markContentDirty?.();
        return;
      }

      if (mode === 'immediate') {
        performSync(currentObject);
      } else if (mode === 'onIdle') {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => performSync(currentObject), idleDelay);
      }
    };

    const unsubscribe = store.subscribe(resolvedPath, handleDataChange);
    return () => {
      unsubscribe();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [store, resolvedPath, mode, idleDelay, performSync, autoSave, markContentDirty]);

  // Manual-mode flush: when the builder bumps `flushVersion`, persist this
  // unit's pending content. For a shared-syncable group only the OWNER posts
  // (one coalesced write of the latest, mirror-synced data); standalone units
  // post their own. flushVersion 0 = initial mount → no-op.
  useEffect(() => {
    if (!flushVersion || resolvedPath === null) return;
    if (mergeKeyRef.current) {
      const owner = getOwner(mergeKeyRef.current);
      if (owner === memberRef.current && owner.pending) {
        owner.pending = false;
        performSync(store.get(resolvedPath));
      }
      return;
    }
    if (memberRef.current.pending) {
      memberRef.current.pending = false;
      performSync(store.get(resolvedPath));
    }
  }, [flushVersion, resolvedPath, performSync, store]);

  // Watch for manual trigger
  useEffect(() => {
    if (!store || triggerPath === null) return;

    const handleTriggerChange = () => {
      if (store.get(triggerPath) === true) {
        store.set(triggerPath, false);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        retryCountRef.current = 0;
        performSync(store.get(resolvedPath));
      }
    };

    const unsubscribe = store.subscribe(triggerPath, handleTriggerChange);
    return () => unsubscribe();
  }, [store, triggerPath, resolvedPath, performSync]);

  return (
    <ActionDependant {...props} attributesHolderRef={eventTargetRef}>
      <span ref={eventTargetRef} style={{ display: 'none' }} />
    </ActionDependant>
  );
};
